import { createEl } from "@/client/utils/createEl";
import * as styles from "./rnd-only-tag.component.css";

export const kRndOnlyTooltip = "This model is evaluation only, gated to limited research audiences";

export interface RndOnlyTagOptions {
    /**
     * "compact" is the home-card form: "R&D" on a pill, where the full label
     * would crowd the card. Defaults to "full", "R&D Only".
     */
    readonly size?: "full" | "compact";
    /** Which side of the tag the tooltip opens on. Defaults to "below". */
    readonly tooltipPlacement?: "above" | "below";
    /** Which edge of the tag the tooltip lines up with. Defaults to "end". */
    readonly tooltipAlign?: "start" | "end";
    /**
     * Whether the tag takes keyboard focus, so a keyboard or a tap can reveal
     * the tooltip. Defaults to true. Pass false where the tag sits inside a
     * link, which is already the focus stop.
     */
    readonly isFocusable?: boolean;
}

let tagCount = 0;

/**
 * The "R&D Only" pill for apps and capabilities that run an evaluation-only
 * model (see `ApiEndpoint.isEvaluationOnly` and docs/09-model-hosting.md).
 */
export const createRndOnlyTag = (options: RndOnlyTagOptions = {}): HTMLElement => {
    tagCount += 1;
    const tooltipId = `rnd-only-tooltip-${tagCount}`;
    const placementClass =
        options.tooltipPlacement === "above" ? styles.tooltipAbove : styles.tooltipBelow;
    const alignClass = options.tooltipAlign === "start" ? styles.tooltipStart : styles.tooltipEnd;
    const tooltip = createEl(
        "span",
        `${styles.tooltip} ${alignClass} ${placementClass}`,
        kRndOnlyTooltip,
        (el) => {
            el.id = tooltipId;
            el.setAttribute("role", "tooltip");
        },
    );
    const isCompact = options.size === "compact";
    return createEl(
        "span",
        isCompact ? `${styles.tag} ${styles.tagCompact}` : styles.tag,
        [document.createTextNode(isCompact ? "R&D" : "R&D Only"), tooltip],
        (el) => {
            el.setAttribute("aria-describedby", tooltipId);
            if (options.isFocusable !== false) {
                el.tabIndex = 0;
            }
        },
    );
};

/**
 * Shows or hides a tag. `el.hidden` alone does not hide an element whose class
 * sets `display`, and the tag's class does, so this sets the style directly.
 */
export const setRndOnlyTagVisible = (el: HTMLElement, isVisible: boolean): void => {
    el.style.display = isVisible ? "" : "none";
};
